// Ritocchi alla copia della pagina usata per lo screenshot della segnalazione (captureScreenshot.ts).
// La cattura usa modern-screenshot (SVG foreignObject: disegna il browser stesso, quindi testi con
// sfumatura, ombre e caratteri escono come a schermo). Restano da gestire qui:
//
// 1. Dati sensibili. I campi di testo, gli elementi marcati data-sensitive e le foto profilo vengono
//    sfocati nella COPIA (onCloneEachNode), il DOM vero non si tocca.
// 2. Elementi da non fotografare: avvisi (toast), l'overlay "Cattura screenshot in corso" e tutto
//    cio' che ha data-screenshot-ignore (o il vecchio data-html2canvas-ignore).
// 3. Animazioni. La copia diventa un'immagine SVG: un'animazione rimasta nello stile copiato
//    ripartirebbe da zero (es. barra laterale con fadeIn da opacita' 0 = fascia vuota). Nella copia
//    si tolgono animazioni e transizioni; nella pagina vera quelle finite si portano subito alla fine.
// 4. Loghi di altri siti. modern-screenshot scarica le immagini con fetch in modalita' CORS e di
//    default usa la cache del browser (cache: 'force-cache'). La pagina carica i loghi
//    (es. api.companyenrich.com) senza CORS: la copia in cache non ha Access-Control-Allow-Origin e la
//    richiesta CORS fallisce. Per le immagini di altri siti si usa l'opzione fetch.bypassingCache
//    (RegExp): la libreria aggiunge un parametro all'indirizzo e la richiesta parte davvero.

/** Campi e celle con dati sensibili: testo sostituito da pallini e sfocato. */
export const SENSITIVE_SELECTOR = [
  'input[type="text"]',
  'input[type="email"]',
  'input[type="tel"]',
  'input[type="number"]',
  'input[type="password"]',
  'input[type="search"]',
  'textarea',
  '[data-sensitive]',
].join(',');

/** Foto profilo: sfocate. */
export const AVATAR_SELECTOR = 'img[alt*="rofile"], img[alt*="vatar"], .avatar, .profile-image';

/** Elementi esclusi dallo screenshot. */
export const EXCLUDED_SELECTOR = [
  '[role="status"]',
  '.toast-container',
  '[data-sonner-toaster]',
  '[data-screenshot-ignore]',
  '[data-html2canvas-ignore]',
].join(',');

const MAX_MASK_LENGTH = 20;

/** Testo mascherato: un pallino per carattere, al massimo 20. */
export function maskValue(value: string | null | undefined): string {
  return value ? '•'.repeat(Math.min(value.length, MAX_MASK_LENGTH)) : '';
}

const isElement = (node: Node): node is Element => node.nodeType === 1;

/** Filtro di modern-screenshot (riceve i nodi della pagina vera): false = escluso con i figli. */
export function shouldCaptureNode(node: Node): boolean {
  if (!isElement(node)) return true;
  try {
    return !node.matches(EXCLUDED_SELECTOR);
  } catch {
    return true;
  }
}

/** onCloneEachNode di modern-screenshot: ritocca ogni nodo della COPIA. */
export function prepareClonedNode(node: Node): void {
  if (!isElement(node)) return;
  const el = node as HTMLElement;
  if (!el.style) return;

  // Animazioni e transizioni: la copia resta ferma sullo stile gia' calcolato.
  el.style.setProperty('animation', 'none', 'important');
  el.style.setProperty('transition', 'none', 'important');

  if (el.matches(SENSITIVE_SELECTOR)) {
    if (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA') {
      // modern-screenshot copia il valore del campo nell'attributo value della copia
      const masked = maskValue(el.getAttribute('value'));
      el.setAttribute('value', masked);
      if (el.tagName === 'TEXTAREA') el.textContent = masked;
    }
    el.style.setProperty('filter', 'blur(4px)');
    el.style.setProperty('user-select', 'none');
  } else if (el.matches(AVATAR_SELECTOR)) {
    el.style.setProperty('filter', 'blur(6px)');
  }
}

/**
 * Nella pagina vera: le animazioni finite (es. fadeIn all'apertura) vanno subito allo stato finale,
 * cosi' lo stile copiato e' quello definitivo. Le infinite (spinner) si lasciano stare.
 */
export function finishRunningAnimations(doc: Pick<Document, 'getAnimations'>): void {
  const anims = typeof doc.getAnimations === 'function' ? doc.getAnimations() : [];
  for (const anim of anims) {
    try {
      const timing = anim.effect?.getComputedTiming?.();
      if (timing && timing.endTime === Infinity) continue;
      anim.finish();
    } catch {
      /* animazione non chiudibile: resta com'e' */
    }
  }
}

const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Indirizzi http(s) di altri siti: per questi modern-screenshot salta la cache del browser
 * (opzione fetch.bypassingCache). Stessa origine, data: e blob: esclusi.
 */
export function crossOriginUrlPattern(pageOrigin: string): RegExp {
  return new RegExp(`^(?!${escapeRegExp(pageOrigin)}(?:[/?#]|$))https?://`, 'i');
}
